import {
  useClientPortalAPI,
  useRequest,
  useSubmit,
  errorIsStatus,
  APIError,
  defaultParseError,
} from "@certn/api";
import type { components } from "@certn/api/generated-schemas/client-portal";
import {
  BasicButton,
  ButtonGroup,
  Card,
  Container,
  Loader,
  PrimaryButton,
  Divider,
  Text,
  AddressFormFormikField,
  showToast,
  SectionMessage,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { Form, Formik } from "formik";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { useClientAccount } from "../../../hooks/useClientAccount";
import { useSubsidiaryCheckEligibility } from "../../../hooks/useSubsidiaryCheckEligibility";
import { useOrderBillingProfile } from "../../order/steps/useOrderBillingProfile";
import { BillingProfileForm } from "../BillingProfileForm";
import { useBillingProfileSchema } from "../useBillingProfileSchema";
import { PaymentProfileChangeConsentModal } from "./PaymentProfileChangeConsentModal";
import { BillingNetTerms } from "./BillingNetTerms";

type BillingProfilePaymentProfileChangePreviewBalance =
  components["schemas"]["BillingProfilePaymentProfileChangePreviewBalance"];
type BillingProfilePaymentProfileChangePreviewResponse =
  components["schemas"]["BillingProfilePaymentProfileChangePreviewResponse"];
type BillingProfileFormValues =
  components["schemas"]["BillingProfileDetail"] & {
    payment_profile_id?: string | null;
  };

interface PendingConsent {
  breakdown: BillingProfilePaymentProfileChangePreviewBalance[];
  resolve: (consent: boolean | null) => void;
}

interface ClientBillingProfileProps {
  readOnly: boolean;
}

export function ClientBillingProfileDetailsForm({
  readOnly,
}: ClientBillingProfileProps) {
  const { t } = useTranslation();
  const { billingProfileId } = useParams();
  const { client } = useClientAccount();
  const { mutate: mutateOrderBillingProfile } = useOrderBillingProfile();

  const successToastVariantRef = useRef<"default" | "charging">("default");
  const [pendingConsent, setPendingConsent] = useState<PendingConsent | null>(
    null
  );

  const { data: billingProfile, mutate } = useClientPortalAPI(
    `/api/internal/client-portal/billing-profiles/${billingProfileId}/`
  );

  const fetchPreview = useRequest();

  const { submit } = useSubmit(
    `/api/internal/client-portal/billing-profiles/${billingProfileId}/update/`,
    {
      throwErrors: true,
      onSuccess: async () => {
        if (successToastVariantRef.current === "charging") {
          showToast(t("Billing profile updated. Charging open invoices now."), {
            type: "success",
          });
        } else {
          showToast(t("Billing profile updated"), { type: "success" });
        }
      },
    }
  );

  const allowedPaymentMethods = client?.allowed_payment_methods;

  const { data } = useClientPortalAPI(
    `/api/internal/client-portal/customer-accounts/${client?.id}/billing-profiles/`,
    { page_size: "all", archived: "ACTIVE" }
  );

  const profileNames =
    useMemo(
      () =>
        data?.results
          ?.filter(
            // filter out currently viewed profile name
            (profile) => profile.profile_name !== billingProfile?.profile_name
          )
          .map((profile) => profile?.profile_name),
      [data, billingProfile?.profile_name]
    ) ?? [];

  const { isEligibleForSubsidiaryCheck } = useSubsidiaryCheckEligibility();

  const schema = useBillingProfileSchema({
    profileNames,
    skipPaymentProfileValidation: isEligibleForSubsidiaryCheck,
  });

  useEffect(() => {
    mutateOrderBillingProfile();
  }, [mutateOrderBillingProfile]);

  if (!billingProfile) {
    return <Loader />;
  }

  const previewPath = `/api/internal/client-portal/billing-profiles/${billingProfileId}/payment-profile-change-preview/`;

  return (
    <Card className={styles.card}>
      <PaymentProfileChangeConsentModal
        isOpen={pendingConsent !== null}
        currencyBreakdown={pendingConsent?.breakdown ?? []}
        onCancel={() => {
          pendingConsent?.resolve(null);
        }}
        onSaveWithoutCharging={() => {
          pendingConsent?.resolve(false);
        }}
        onSaveAndCharge={() => {
          pendingConsent?.resolve(true);
        }}
      />

      <Formik
        initialValues={{
          ...billingProfile,
          // API returns the whole object, not just the id
          payment_profile_id: billingProfile.payment_profile?.id,
        }}
        onSubmit={async (values: BillingProfileFormValues, helpers) => {
          const initialPpId = billingProfile?.payment_profile?.id ?? null;
          const nextPpId = values.payment_profile_id ?? null;
          const isReplace = initialPpId !== null && initialPpId !== nextPpId;

          const runSubmit = async (
            body: BillingProfileFormValues & {
              auto_collect_open_invoices?: boolean;
            },
            helpersInner: typeof helpers,
            toastVariant: "default" | "charging"
          ) => {
            successToastVariantRef.current = toastVariant;
            try {
              await submit(body);
              const freshValues = await mutate();

              helpersInner.resetForm({
                values: freshValues
                  ? {
                      ...freshValues,
                      payment_profile_id: freshValues?.payment_profile?.id,
                    }
                  : values,
              });
            } catch (error) {
              if (!errorIsStatus(error, 409)) {
                const message =
                  error instanceof APIError
                    ? defaultParseError(error) ??
                      error.detail ??
                      t("Something went wrong")
                    : t("Something went wrong");
                showToast(message, { type: "error" });
              }
            } finally {
              // Always reset so a failed submit can't leak the "charging"
              // variant into the next save's success toast.
              successToastVariantRef.current = "default";
            }
          };

          const trySubmitDefault = async (body: BillingProfileFormValues) =>
            await runSubmit(body, helpers, "default");

          if (!isReplace) {
            await trySubmitDefault(values);
            return;
          }

          let preview: BillingProfilePaymentProfileChangePreviewResponse;
          try {
            preview =
              await fetchPreview<BillingProfilePaymentProfileChangePreviewResponse>(
                previewPath
              );
          } catch {
            showToast(t("Something went wrong"), { type: "error" });
            return;
          }

          if (!preview.any_open_balance) {
            await trySubmitDefault(values);
            return;
          }

          const consent = await new Promise<boolean | null>((resolve) => {
            setPendingConsent({
              breakdown: preview.currency_breakdown,
              resolve,
            });
          });

          setPendingConsent(null);

          if (consent === null) {
            return;
          }

          await runSubmit(
            { ...values, auto_collect_open_invoices: consent },
            helpers,
            consent ? "charging" : "default"
          );
        }}
        validationSchema={schema}
        enableReinitialize
      >
        {({ isSubmitting, dirty, values }) => {
          const isApplicantPayer = values.payer_option === "APPLICANT";

          return (
            <Form>
              <Container>
                <SectionMessage
                  type="info"
                  icon="info"
                  sectionTitle={t("Updating billing profiles")}
                >
                  {t(
                    "Not all details can be changed once the profile has been created. If you need to make changes, please create a new billing profile."
                  )}
                </SectionMessage>
                <Text style="form/section-heading">
                  {t("Billing profile details")}
                </Text>
                <BillingProfileForm
                  allowedPaymentMethods={
                    allowedPaymentMethods
                      ? [...allowedPaymentMethods]
                      : undefined
                  }
                />
                <BillingNetTerms
                  payment_method={values.payment_method}
                  net_terms={values.net_terms}
                />
                {!isApplicantPayer && (
                  <>
                    <Divider />
                    <Text style="form/section-heading">
                      {t("Billing address")}
                    </Text>
                    <AddressFormFormikField
                      testID="billing_information_address"
                      name="address"
                      isDisabled={readOnly}
                    />
                  </>
                )}
                {!readOnly && (
                  <ButtonGroup>
                    <BasicButton
                      type="reset"
                      size="medium"
                      testID="cancel-button"
                      isDisabled={!dirty}
                    >
                      {t("Reset")}
                    </BasicButton>
                    <PrimaryButton
                      type="submit"
                      size="medium"
                      testID="save-billing-profile-button"
                      isLoading={isSubmitting}
                      isDisabled={!dirty}
                    >
                      {t("Save")}
                    </PrimaryButton>
                  </ButtonGroup>
                )}
              </Container>
            </Form>
          );
        }}
      </Formik>
    </Card>
  );
}

const styles = {
  card: css({
    borderWidth: "var(--space-025)",
    borderRadius: "var(--border-radius-3)",
    padding: "var(--space-5)",
  }),
};
